import { Link, useRouterState } from "@tanstack/react-router";
import {
  BotIcon,
  BuildingIcon,
  ChartNoAxesCombinedIcon,
  ClipboardListIcon,
  FileSearchIcon,
  FolderIcon,
  IdCardIcon,
  LayoutDashboardIcon,
  MessageCircleIcon,
  RadarIcon,
  ShieldAlertIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UsersIcon,
  WorkflowIcon,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { Logo } from "@/components/brand/logo";
import { HeaderUtilityActions } from "@/components/shared/navigation/header-utility-actions";
import { MoreMenu } from "@/components/shared/navigation/more-menu";
import { ProfileMenu } from "@/components/shared/navigation/profile-menu";
import { useAuth } from "@/features/auth/use-auth";
import {
  hasPermission,
  type PermissionModule,
  type PermissionAwareUser,
} from "@/lib/authorization/permissions";
import { cn } from "@/lib/utils";

export interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  permissionModule?: PermissionModule;
}

const NAVIGATION_ITEMS: readonly NavItem[] = [
  {
    to: "/dashboard",
    labelKey: "common:nav.dashboard",
    icon: LayoutDashboardIcon,
    permissionModule: "dashboard",
  },
  {
    to: "/ai-apps",
    labelKey: "common:nav.aiApps",
    icon: SparklesIcon,
    permissionModule: "ai_apps",
  },
  {
    to: "/review-queue",
    labelKey: "common:nav.reviewQueue",
    icon: ClipboardListIcon,
    permissionModule: "review_queue",
  },
  {
    to: "/agent-inventory",
    labelKey: "common:nav.agentInventory",
    icon: BotIcon,
    permissionModule: "agent_inventory",
  },
  {
    to: "/findings",
    labelKey: "common:nav.findings",
    icon: ShieldAlertIcon,
    permissionModule: "findings",
  },
  {
    to: "/evaluators",
    labelKey: "common:nav.evaluators",
    icon: ChartNoAxesCombinedIcon,
    permissionModule: "evaluators",
  },
  {
    to: "/projects",
    labelKey: "common:nav.projects",
    icon: FolderIcon,
    permissionModule: "projects",
  },
  {
    to: "/audit-explorer",
    labelKey: "common:nav.auditExplorer",
    icon: FileSearchIcon,
    permissionModule: "audit_explorer",
  },
  {
    to: "/model-cards",
    labelKey: "common:nav.modelCards",
    icon: IdCardIcon,
    permissionModule: "model_cards",
  },
  {
    to: "/workflow-builder",
    labelKey: "common:nav.workflowBuilder",
    icon: WorkflowIcon,
    permissionModule: "workflow_builder",
  },
  {
    to: "/discovery",
    labelKey: "common:nav.discovery",
    icon: RadarIcon,
    permissionModule: "discovery",
  },
  {
    to: "/baa-preflight",
    labelKey: "common:nav.baaPreflight",
    icon: ShieldCheckIcon,
    permissionModule: "baa_preflight",
  },
  {
    to: "/conversations",
    labelKey: "common:nav.conversations",
    icon: MessageCircleIcon,
    permissionModule: "conversations",
  },
];

const PRIMARY_NAVIGATION_COUNT = 5;

function buildNavigationItems(
  user: PermissionAwareUser | null | undefined,
): readonly NavItem[] {
  const items = NAVIGATION_ITEMS.filter(
    (item) =>
      !item.permissionModule || hasPermission(user, item.permissionModule, "view"),
  );
  if (
    user &&
    (hasPermission(user, "tenant_admin", "view") ||
      hasPermission(user, "subscription", "view"))
  ) {
    items.push({
      to: "/tenant-admin",
      labelKey: "common:nav.tenantAdmin",
      icon: UsersIcon,
      permissionModule: "tenant_admin",
    });
  }
  if (user?.role === "platform_admin") {
    items.push({
      to: "/platform-admin",
      labelKey: "common:nav.platformAdmin",
      icon: BuildingIcon,
    });
  }
  return items;
}

function isNavigationItemActive(item: NavItem, pathname: string): boolean {
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

export function TopNav() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const navigationItems = buildNavigationItems(user);
  const primaryItems = navigationItems.slice(0, PRIMARY_NAVIGATION_COUNT);
  const moreItems = navigationItems.slice(PRIMARY_NAVIGATION_COUNT);

  return (
    <header className="flex h-header items-center justify-between border-b border-nav-light-border bg-nav-background px-4 font-nav sm:px-6">
      <Link to="/dashboard" className="shrink-0" aria-label={t("common:appName")}>
        <Logo size="md" />
      </Link>
      <nav
        aria-label={t("common:appName")}
        className="flex h-full min-w-0 flex-1 items-end justify-start gap-2 overflow-x-auto nav:ml-4 nav-wide:gap-4 nav-xl:gap-6"
      >
        {primaryItems.map((item) => (
          <PrimaryNavItem
            key={item.to}
            item={item}
            isActive={isNavigationItemActive(item, pathname)}
          />
        ))}
        <MoreMenu items={moreItems} />
      </nav>
      <div className="flex shrink-0 items-center gap-2 md:gap-4">
        <HeaderUtilityActions />
        <ProfileMenu />
      </div>
    </header>
  );
}

interface PrimaryNavItemProps {
  item: NavItem;
  isActive: boolean;
}

function PrimaryNavItem({ item, isActive }: PrimaryNavItemProps) {
  const { t } = useTranslation();

  return (
    <Link
      to={item.to}
      className={cn(
        "flex h-nav shrink-0 items-center justify-center gap-2 border-b-4 border-transparent text-base font-medium not-italic text-nav-text transition-colors",
        isActive
          ? "border-nav-active text-nav-active hover:text-nav-active"
          : "hover:text-nav-hover-text",
      )}
    >
      <item.icon aria-hidden="true" className="h-5 w-5" />
      <span>{t(item.labelKey)}</span>
    </Link>
  );
}
